/**
 * Studios on the wire (PLAN §5.1, §7.1a, D22): the "rig" — devices, mappings
 * from layer to device, source/output layers, piece types, actions, adlib
 * panels — as one versioned document per studio.
 *
 * **The contract carries the ENVELOPE, not the config's shape.** `config` is
 * an opaque JSON object here; what a valid config is belongs to the engine
 * (`@acp/engine/studioConfig.js`, which compiles piece types into timeline
 * objects and so is the one place that can say what it needs). The handlers
 * validate `config` with an injected validator (`StudioConfigValidator` in
 * `@acp/handlers/ports.js`), so the contract never drifts from the engine and
 * the engine's schema can evolve without a contract release. A failing config
 * is `validation-failed` with the validator's issues, prefixed `config.`.
 *
 * **Optimistic concurrency.** Every write bumps `version` by one; an update
 * (`PUT`) names the version it was made from and is refused (`conflict`) when
 * the stored studio has moved on — re-read, re-apply, retry. A delete may name
 * one too (`?version=`).
 *
 * **Snapshots (D22).** A production copies the studio's config when it is
 * created (`productions.ts`), so editing a studio never changes a production
 * that already exists.
 *
 * `.strict()` everywhere, as the rest of the contract; ids are key segments
 * (`entityIdSchema`: no `#`), so a studio id can never forge a DynamoDB key.
 */
import { jsonValueSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { entityIdSchema } from './tenancy.js';

/** A studio id: the same alphabet a group's `scope.studioIds` holds (D14), so a grant can name it. */
export const studioIdSchema = entityIdSchema;
/** A site id (phase 4 enrols sites; until then a studio may name one, or none). */
export const siteIdSchema = entityIdSchema;

/**
 * The most characters a studio config may serialise to. A production item
 * carries a copy (D22), and a DynamoDB item is at most 400 KB; the cloud's
 * body limit (256 KiB) bounds it first on a write, this bounds it on every
 * host alike.
 */
export const MAX_STUDIO_CONFIG_CHARS = 200_000;

/**
 * The config document: a JSON object, otherwise opaque to the contract (the
 * module comment). Its size is bounded here so every host refuses the same
 * oversized document.
 */
export const studioConfigSchema = z
  .record(z.string(), jsonValueSchema)
  .refine((config) => JSON.stringify(config).length <= MAX_STUDIO_CONFIG_CHARS, {
    message: `the studio config serialises to more than ${String(MAX_STUDIO_CONFIG_CHARS)} characters`,
  });
export type StudioConfig = z.infer<typeof studioConfigSchema>;

const studioNameSchema = z.string().trim().min(1).max(200);
const isoSchema = z.iso.datetime({ offset: true });

/** A studio without its config: what a listing shows. */
export const studioSummarySchema = z
  .object({
    studioId: studioIdSchema,
    name: studioNameSchema,
    /** The site it belongs to, or null for a studio tied to no site (resource scope, D14). */
    siteId: siteIdSchema.nullable(),
    /** 1 on create, +1 per write: the optimistic-concurrency token. */
    version: z.number().int().positive(),
    createdAt: isoSchema,
    updatedAt: isoSchema,
    /** Who wrote this version (the token's subject). */
    updatedBy: z.string().min(1).max(128),
  })
  .strict();
export type StudioSummary = z.infer<typeof studioSummarySchema>;

/** A studio with its config. */
export const studioSchema = studioSummarySchema.extend({ config: studioConfigSchema }).strict();
export type Studio = z.infer<typeof studioSchema>;

export const createStudioRequestSchema = z
  .object({
    /** Client-chosen id (a retried create returns the studio as it is); the server mints a ULID when absent. */
    studioId: studioIdSchema.optional(),
    name: studioNameSchema,
    siteId: siteIdSchema.nullable(),
    config: studioConfigSchema,
  })
  .strict();
export type CreateStudioRequest = z.infer<typeof createStudioRequestSchema>;

export const updateStudioRequestSchema = z
  .object({
    /** The version this update was made from; the stored one must still be it (`conflict` otherwise). */
    version: z.number().int().positive(),
    name: studioNameSchema,
    siteId: siteIdSchema.nullable(),
    config: studioConfigSchema,
  })
  .strict();
export type UpdateStudioRequest = z.infer<typeof updateStudioRequestSchema>;

export const studioPathSchema = z.object({ studioId: studioIdSchema.describe('The studio’s id.') }).strict();

export const deleteStudioQuerySchema = z
  .object({
    version: z.coerce.number().int().positive().optional().describe('Delete only if the studio is still at this version (`conflict` otherwise).'),
  })
  .strict();

export const studioResponseSchema = z.object({ studio: studioSchema }).strict();
export const listStudiosResponseSchema = z.object({ studios: z.array(studioSummarySchema) }).strict();
